// src/components/personal/sections/ClientsSection/utils/clientCache.ts
import { Client } from '../types/types';

interface CachedData {
  clients: Client[];
  timestamp: number;
  businessId: number | string;
}

interface CacheInfo {
  isCached: boolean;
  lastUpdated?: Date;
  expiration?: Date;
  itemsCount?: number;
}

/**
 * Класс для кеширования данных клиентов
 * Используется для улучшения производительности при работе с большим количеством клиентов
 */
class ClientCache {
  private cache: CachedData | null = null;
  private readonly maxAge: number; // Время жизни кеша в миллисекундах
  
  /**
   * Создает экземпляр кеша клиентов
   * @param maxAgeMinutes Время жизни кеша в минутах (по умолчанию 15)
   */
  constructor(maxAgeMinutes: number = 15) {
    this.maxAge = maxAgeMinutes * 60 * 1000;
  }
  
  /**
   * Сохраняет данные клиентов в кеше
   * 
   * @param clients Список клиентов
   * @param businessId ID бизнеса
   */
  setClients(clients: Client[], businessId: number | string): void {
    this.cache = {
      clients,
      timestamp: Date.now(),
      businessId
    };
    
    console.log(`Кеш обновлен: ${clients.length} клиентов для бизнеса ${businessId}`);
  }
  
  /**
   * Получает данные клиентов из кеша
   * 
   * @param businessId ID бизнеса
   * @returns Список клиентов или null, если кеш отсутствует или недействителен
   */
  getClients(businessId: number | string): Client[] | null {
    // Если кеш отсутствует или относится к другому бизнесу
    if (!this.cache || this.cache.businessId !== businessId) {
      return null;
    }
    
    // Проверяем, не устарел ли кеш
    if (this.isStale()) {
      console.log('Кеш устарел, требуется обновление');
      return null;
    }
    
    return this.cache.clients;
  }
  
  /**
   * Проверяет, устарел ли кеш
   * 
   * @returns true, если кеш устарел
   */
  isStale(): boolean {
    if (!this.cache) return true;
    
    const now = Date.now();
    const age = now - this.cache.timestamp;
    
    return age > this.maxAge;
  }
  
  /**
   * Получает информацию о времени жизни кеша
   * 
   * @returns Объект с информацией или null, если кеш отсутствует
   */
  getCacheInfo(): CacheInfo {
    if (!this.cache) {
      return {
        isCached: false
      };
    }
    
    // Используем непосредственно Date.now() без создания промежуточных переменных
    
    return {
      isCached: true,
      lastUpdated: new Date(this.cache.timestamp),
      expiration: new Date(this.cache.timestamp + this.maxAge),
      itemsCount: this.cache.clients.length
    };
  }
  
  /**
   * Очищает кеш
   */
  clearCache(): void {
    this.cache = null;
    console.log('Кеш очищен');
  }
  
  /**
   * Проверяет наличие данных в кеше для указанного бизнеса
   * 
   * @param businessId ID бизнеса
   * @returns true, если в кеше есть актуальные данные для бизнеса
   */
  hasValidCache(businessId: number | string): boolean {
    if (!this.cache || this.cache.businessId !== businessId) {
      return false;
    }
    
    return !this.isStale();
  }
}

// Экспортируем синглтон для использования во всем приложении
export const clientCache = new ClientCache();

// Также экспортируем класс для возможности создания нескольких экземпляров
export default ClientCache;